import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { SearchLg } from '@untitled-ui/icons-react'
import { MyButton, MyTextField } from '@interstellar-component'
import { FILE_KIND_LABEL } from '@src/services/consentSettings'
import ENQUIRY_CATEGORY_LABEL from '@src/services/enquiryCategory'
import Overlay from '@src/components/Overlay'
import { SEARCH_MAX_LENGTH, nikMismatchText, trayReasonText } from '../constants'
import { assignableKinds, fileNikMismatch, pendingKey, rowNeeds } from '../fileMatcher'
import Notice from './Notice'
import { LocalFileView, RowIdentity } from './FilePreview'

// The row list shows at most this many rows; the search narrows it down.
const LIST_LIMIT = 100

/** Whether a row is the one HR searches for: name, email, Employee ID or NIK digits. */
export function rowMatchesSearch(row, search) {
  const q = String(search || '').trim().toLowerCase()
  if (!q) return true
  const texts = [row.full_name, row.email, row.invite_email, row.employee_code].map((v) =>
    String(v || '').toLowerCase()
  )
  if (texts.some((text) => text.includes(q))) return true
  const digits = q.replace(/\s+/g, '')
  return /^\d{3,16}$/.test(digits) && String(row.nik || '').includes(digits)
}

function whyNot(row, family) {
  if (family === 'photo') {
    return rowNeeds(row, 'ktp_photo') === 'reused'
      ? trayReasonText({ reason: 'ktp_reused' })
      : 'This row needs no KTP photo.'
  }
  return 'This row needs no document from HR.'
}

/**
 * Put one file on a row, by hand: a file that found no row by itself (the
 * tray) or one to move to another row ("Reassign"). HR searches the row,
 * picks the file kind the row still takes, and sees the file beside the row's
 * e-KTP data. A file whose name carries another person's NIK cannot be put
 * on the row (the backend would refuse it, 409 file_nik_mismatch).
 */
export default function AssignFileModal({ open, entry, targets, pending, keyMode, onAssign, onClose }) {
  const [search, setSearch] = useState('')
  const [rowId, setRowId] = useState(null)
  const [kind, setKind] = useState(null)

  const suggested = useMemo(() => entry?.rowIds || [], [entry])
  // Every opening starts fresh (the search field itself is new each time).
  useEffect(() => {
    if (!open) return
    setSearch('')
    setRowId(suggested.length === 1 ? suggested[0] : null)
    setKind(null)
  }, [open, entry, suggested])

  const rowsById = useMemo(() => new Map(targets.map((row) => [row.id, row])), [targets])
  const listed = useMemo(() => {
    const suggestedRows = suggested.map((id) => rowsById.get(id)).filter(Boolean)
    const rest = targets.filter((row) => !suggested.includes(row.id))
    return [...suggestedRows, ...rest].filter((row) => rowMatchesSearch(row, search))
  }, [targets, suggested, rowsById, search])

  const row = rowId ? rowsById.get(rowId) : null
  const kinds = row && entry ? assignableKinds(row, entry.family) : []
  // HR's pick; else the kind the file itself names (a photo, a keyword);
  // else the row's only kind, but only for a file that names none (a letter
  // is never offered as the T&C by default).
  let chosenKind = null
  if (kind && kinds.includes(kind)) chosenKind = kind
  else if (entry?.kind) chosenKind = kinds.includes(entry.kind) ? entry.kind : null
  else if (kinds.length === 1) [chosenKind] = kinds

  if (!open || !entry) return null

  const mismatch = row ? fileNikMismatch(entry.file.name, row.nik) : false
  const sameTarget = Boolean(row && chosenKind && entry.rowId === row.id && entry.kind === chosenKind)
  const waiting = row && chosenKind ? pending.get(pendingKey(row.id, chosenKind)) : null
  const replacesWaiting = waiting && waiting.id !== entry.id ? waiting : null
  const replacesSaved = row && chosenKind && rowNeeds(row, chosenKind) === 'staged' ? row.files[chosenKind] : null
  const canAssign = Boolean(row && chosenKind && !mismatch && !sameTarget)

  return (
    <Overlay
      open={open}
      onClose={onClose}
      title={entry.rowId ? 'Move this file to another row' : 'Assign this file to a row'}
      subtitle={
        <span className="break-all">
          {entry.file.name}
          {entry.reason ? ` · ${trayReasonText(entry, { keyMode })}` : ''}
        </span>
      }
      maxWidth={1100}
      footer={
        <>
          <MyButton type="button" color="secondary" variant="outlined" size="md" onClick={onClose}>
            Cancel
          </MyButton>
          <MyButton
            type="button"
            color="primary"
            variant="filled"
            size="md"
            disabled={!canAssign}
            onClick={() => onAssign({ rowId: row.id, rowNo: row.row_no, kind: chosenKind })}
          >
            {replacesWaiting || replacesSaved ? 'Replace' : 'Assign'}
          </MyButton>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* ── Row + kind ───────────────────────────────────────────── */}
        <div
          className={
            'flex w-full min-w-0 shrink-0 flex-col gap-3 border-b border-gray-200 p-4 ' +
            'lg:w-[400px] lg:border-b-0 lg:border-r'
          }
        >
          <MyTextField
            id="input-search-assign-row"
            placeholder="Search name, email, Employee ID or NIK"
            maxLength={SEARCH_MAX_LENGTH}
            startAdornment={<SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />}
            focusColor="#14378E"
            onChangeForm={(e) => setSearch(e.target.value)}
          />
          <div role="listbox" aria-label="Rows waiting for files" className="flex max-h-[40vh] flex-col gap-1 overflow-auto">
            {listed.length === 0 && <p className="px-2 py-3 text-sm text-gray-500">No row waiting for files matches.</p>}
            {listed.slice(0, LIST_LIMIT).map((candidate) => {
              const selected = candidate.id === rowId
              return (
                <button
                  key={candidate.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    setRowId(candidate.id)
                    setKind(null)
                  }}
                  className={`flex flex-col rounded-lg border px-3 py-2 text-left ${
                    selected ? 'border-brand/600 bg-brand/50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm font-medium text-gray-900">
                    Row {candidate.row_no} · {candidate.full_name || '—'}
                    {suggested.includes(candidate.id) && (
                      <span className="ml-2 text-xs font-normal text-brand/700">suggested</span>
                    )}
                  </span>
                  <span className="truncate text-xs text-gray-500">
                    {[
                      ENQUIRY_CATEGORY_LABEL[candidate.category],
                      candidate.nik,
                      candidate.email,
                      candidate.employee_code && `ID ${candidate.employee_code}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
              )
            })}
            {listed.length > LIST_LIMIT && (
              <p className="px-2 py-1 text-xs text-gray-500">
                {listed.length - LIST_LIMIT} more rows: search to narrow the list.
              </p>
            )}
          </div>

          {row && (
            <div className="flex flex-col gap-2">
              {kinds.length === 0 ? (
                <Notice tone="warning" title={whyNot(row, entry.family)} />
              ) : (
                <fieldset className="flex flex-col gap-1.5">
                  <legend className="mb-1 text-sm font-medium text-gray-700">File of row {row.row_no}</legend>
                  {kinds.map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-gray-800">
                      <input
                        type="radio"
                        name="assign-kind"
                        value={k}
                        checked={chosenKind === k}
                        onChange={() => setKind(k)}
                      />
                      {FILE_KIND_LABEL[k] || k}
                      {rowNeeds(row, k) === 'staged' && <span className="text-xs text-gray-500">(saved: replaces it)</span>}
                    </label>
                  ))}
                </fieldset>
              )}
              {mismatch && (
                <Notice tone="error" title="This file names another person">
                  <p>{nikMismatchText(row.row_no)}</p>
                </Notice>
              )}
              {sameTarget && <p className="text-xs text-gray-500">The file is already on this row.</p>}
              {!mismatch && replacesWaiting && (
                <p className="text-xs text-warning/700">
                  Replaces {replacesWaiting.file.name}, added before and not saved yet.
                </p>
              )}
              {!mismatch && !replacesWaiting && replacesSaved && (
                <p className="text-xs text-warning/700">Replaces the saved file {replacesSaved.file_name || ''}.</p>
              )}
            </div>
          )}
        </div>

        {/* ── The file beside the row's e-KTP data ─────────────────── */}
        <div className="flex min-h-[50vh] min-w-0 flex-1 flex-col lg:flex-row">
          <div className="flex min-h-[50vh] flex-1 flex-col items-stretch justify-center overflow-auto bg-gray-50 p-3">
            <LocalFileView file={entry.file} />
          </div>
          {row && (
            <div className="w-full shrink-0 overflow-auto border-t border-gray-200 p-4 lg:w-[300px] lg:border-l lg:border-t-0">
              <RowIdentity row={row} />
            </div>
          )}
        </div>
      </div>
    </Overlay>
  )
}

AssignFileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    file: PropTypes.object.isRequired,
    family: PropTypes.oneOf(['photo', 'document']).isRequired,
    kind: PropTypes.string,
    reason: PropTypes.string,
    rowId: PropTypes.string,
    rowIds: PropTypes.arrayOf(PropTypes.string),
  }),
  targets: PropTypes.arrayOf(PropTypes.object).isRequired,
  pending: PropTypes.instanceOf(Map).isRequired,
  keyMode: PropTypes.oneOf(['nik', 'email']).isRequired,
  onAssign: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}
